import { useState, useEffect } from "react";
import "./TopTutors.scss";
import tutor_img from "../../Assets/Tutor/tutor_image.png";
import BadgeIcon from "../../Assets/Tutor/BadgeIcon.svg";
import StarIcon from '@mui/icons-material/Star';
import { ROUTES } from "../../Constants/Constants";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { GetSessionStorageData } from "../../Utilities/Helpers/Helper";

interface ProfileDetails {
  firstname?: string;
  lastname?: string;
  arabic_firstname?: string;
  arabic_lastname?: string;
}

interface TutorSubject {
  courses: string;
  grade_name: string;
}

interface Tutor {
  id?: number;
  profile_image?: string;
  profile_details?: ProfileDetails;
  tutor_subjects?: TutorSubject[];
  rating?: number;
  is_available?: boolean;      
  expiry_date?: string | null; 
  is_slots_available?: boolean; 
  tag?: string;                
}

interface TopTutorsProps {
  tutorList: Tutor[] | undefined | null;
  radius: number;
  lat: string;
  lng: string;
  FilterApplyData: Record<string, any>;
}

interface RootState {
  languageReducer: {
    language: 'English' | 'Arabic';
  };
}

const TopTutors: React.FC<TopTutorsProps> = ({ tutorList, radius, lat, lng, FilterApplyData }) => {
  const [storedTagged, setStoredTagged] = useState<any[] | null>(null);

  /** Check if tutor is expired */
  const isTutorExpired = (tutor: Tutor) => {
    if (tutor.is_available === false) return true;
    if (tutor.expiry_date) {
      const expiry = new Date(tutor.expiry_date);
      return expiry < new Date();
    }
    return false;
  };

  /** Read stored tagged tutors from session/local storage */
  useEffect(() => {
    const read = () => {
      try {
        const raw = GetSessionStorageData("TUTORS_WITH_TAG");
        setStoredTagged(raw ? JSON.parse(raw) : null);
      } catch {
        setStoredTagged(null);
      }
    };
    read();

    const onStorage = (e: StorageEvent) => {
      if (e.key === "TUTORS_WITH_TAG") read();
    };
    const onCustom = (e: any) => {
      if (e?.detail) setStoredTagged(e.detail);
      else read();
    };
    window.addEventListener("storage", onStorage);
    window.addEventListener("TUTORS_WITH_TAG_UPDATED", onCustom as EventListener);
    window.addEventListener("focus", read);

    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("TUTORS_WITH_TAG_UPDATED", onCustom as EventListener);
      window.removeEventListener("focus", read);
    };
  }, []);

  /** Merge and sort tutors: available first, expired at bottom */
  const effectiveTutorList = (() => {
    if (!tutorList || tutorList.length === 0) {
      return Array.isArray(storedTagged) ? storedTagged : [];
    }

    const mergedList = tutorList
      .map((t) => {
        const m = storedTagged?.find((s) => s?.id === t?.id);
        return m ? { ...t, ...m, tag: m.tag ?? t.tag } : t;
      });

    const availableTutors = mergedList.filter(t => !isTutorExpired(t));
    const expiredTutors = mergedList.filter(t => isTutorExpired(t));
    return [...availableTutors, ...expiredTutors];
  })();

  const navigate = useNavigate();
  const { t } = useTranslation();
  const language = useSelector((state: RootState) => state.languageReducer.language);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  /** Skeleton loader */
  useEffect(() => {
    const minLoadingTime = 2000;
    const startTime = Date.now();

    if (tutorList !== undefined && tutorList !== null) {
      const elapsedTime = Date.now() - startTime;
      const remainingTime = minLoadingTime - elapsedTime;

      if (tutorList.length > 0) {
        if (remainingTime > 0) {
          const timer = setTimeout(() => setIsLoading(false), remainingTime);
          return () => clearTimeout(timer);
        } else {
          setIsLoading(false);
        }
      } else {
        const timer = setTimeout(() => setIsLoading(false), Math.max(remainingTime, 1000));
        return () => clearTimeout(timer);
      }
    }
  }, [tutorList]);

  /** Navigation handlers */
  const handlePassCoursecategory = (value: Tutor) => {
    navigate(ROUTES.TUTORS_LIST_VIEW, {
      state: {
        tutor_list: [value],
        search_text: '',
        radius,
        lat,
        lng,
        filter: FilterApplyData,
      },
    });
  };

  const handlePassList = () => {
    navigate(ROUTES.TUTORS_LIST_VIEW, {
      state: {
        tutor_list: effectiveTutorList,
        search_text: '',
        radius,
        lat,
        lng,
        filter: FilterApplyData,
      },
    });
  };

  /** Skeleton UI */
  const renderSkeleton = () => (
    <div className="scroll__wapper">
      {[...Array(5)].map((_, i) => (
        <div className="top_tutors_card mt-2 skeleton-card" key={`skeleton-${i}`}>
          <div className="d-flex">
            <div className="tutor_img skeleton skeleton-circle"></div>
            <div className="ms-2 notranslate">
              <div className="card__title skeleton skeleton-title"></div>
              <div className="badge">
                <div className="BadgeIcon skeleton skeleton-icon"></div>
                <div className="skeleton skeleton-text"></div>
              </div>
            </div>
          </div>
          <div className="d-flex align-items-center">
            <div className="StarIcon skeleton skeleton-star"></div>
            <div className="card__title skeleton skeleton-rating"></div>
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="top_tutors_card_wrapper">
      <div className="top_tutors_title" onClick={handlePassList}>
        {t("Top tutors near you")}
      </div>

      {isLoading ? (
        renderSkeleton()
      ) : effectiveTutorList.length === 0 ? (
        <div className="empty__data">
          <span className="text">{t("No Data Found")}</span>
        </div>
      ) : (
        <div className="scroll__wapper">
          {effectiveTutorList.slice(0, 5).map((val: Tutor, i: number) => (
            <div
              className={`top_tutors_card mt-2 ${isTutorExpired(val) ? "expired" : ""}`}
              key={i}
              onClick={() => handlePassCoursecategory(val)}
            >
              <div className="d-flex">
                <img
                  src={val.profile_image || tutor_img}
                  onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                    e.currentTarget.src = tutor_img;
                  }}
                  alt="tutor_img"
                  className="tutor_img"
                />
                <div className="ms-2 notranslate">
                  <div className="d-flex align-items-center justify-content-between top-line">
                    <div className="card__title">
                      {language === "English"
                        ? `${val.profile_details?.firstname || ""} ${val.profile_details?.lastname || ""}`.trim()
                        : `${val.profile_details?.arabic_firstname || val.profile_details?.firstname || ""} ${val.profile_details?.arabic_lastname || val.profile_details?.lastname || ""}`.trim()}
                    </div>
                  </div>
                  <div className="badge">
                    <img src={BadgeIcon} alt="BadgeIcon" className="BadgeIcon" />
                    <div>
                      {val.tutor_subjects && val.tutor_subjects.length > 0
                        ? `${val.tutor_subjects[0].courses} - ${val.tutor_subjects[0].grade_name}`
                        : ""}
                    </div>
                  </div>
                </div>
              </div>

              <div className="d-flex align-items-center justify-content-between mt-1">
                {val.rating ? (
                  <div className="d-flex align-items-center">
                    <StarIcon className="StarIcon" />
                    <h4 className="card__title">{val.rating}</h4>
                  </div>
                ) : <div></div>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TopTutors;
